import React, { createContext, useContext, useState, useEffect, useCallback } from 'react';
import { authApi } from '../api';

// ─── Permission map per adminRole ─────────────────────────────────────────────
const ROLE_PERMISSIONS = {
  SUPER_ADMIN: ['*'],
  MANAGER:     ['dashboard', 'users.view', 'users.edit', 'battles', 'reports', 'reports.export',
                'notifications', 'activity_logs', 'admins', 'roles.view', 'settings'],
  MARKETING:   ['dashboard', 'battles', 'reports', 'content', 'content.publish', 'notifications'],
  SUPPORT:     ['dashboard', 'users.view', 'battles', 'activity_logs'],
};

// ─── Context ───────────────────────────────────────────────────────────────────
const AuthContext = createContext(null);

export const AuthProvider = ({ children }) => {
  const [user, setUser]       = useState(null);
  const [loading, setLoading] = useState(true);

  // ── Restore session from localStorage on mount ────────────────────────────
  useEffect(() => {
    const savedUser  = localStorage.getItem('wb_admin_user');
    const savedToken = localStorage.getItem('wb_admin_token');
    if (savedUser && savedToken) {
      try {
        setUser(JSON.parse(savedUser));
      } catch {
        localStorage.removeItem('wb_admin_user');
        localStorage.removeItem('wb_admin_token');
      }
    }
    setLoading(false);
  }, []);

  // ── Login ──────────────────────────────────────────────────────────────────
  const login = useCallback(async (email, password) => {
    const res  = await authApi.login(email, password);
    const data = res.data?.data || res.data;

    if (!data?.token) throw new Error('Invalid response from server');

    if (!data.adminRole) {
      throw new Error('Access denied. This account does not have admin privileges.');
    }

    const session = {
      userId:      data.userId,
      name:        data.name,
      email:       data.email,
      profilePic:  data.profilePic || null,
      adminRole:   data.adminRole,
      status:      data.status,
      city:        data.city,
      permissions: ROLE_PERMISSIONS[data.adminRole] || ['dashboard'],
    };

    localStorage.setItem('wb_admin_token', data.token);
    localStorage.setItem('wb_admin_user',  JSON.stringify(session));
    setUser(session);
    return session;
  }, []);

  // ── Logout ─────────────────────────────────────────────────────────────────
  const logout = useCallback(() => {
    setUser(null);
    localStorage.removeItem('wb_admin_token');
    localStorage.removeItem('wb_admin_user');
  }, []);

  // ── Permission check ───────────────────────────────────────────────────────
  const hasPermission = useCallback((permission) => {
    if (!user) return false;
    const perms = user.permissions || [];
    if (perms.includes('*')) return true;
    return perms.includes(permission);
  }, [user]);

  // ── Update local session (after profile edit) ─────────────────────────────
  const updateProfile = useCallback((updates) => {
    const updated = { ...user, ...updates };
    setUser(updated);
    localStorage.setItem('wb_admin_user', JSON.stringify(updated));
  }, [user]);

  const value = {
    user,
    loading,
    login,
    logout,
    hasPermission,
    updateProfile,
    isAuthenticated: !!user,
    isSuperAdmin:    user?.adminRole === 'SUPER_ADMIN',
    adminRole:       user?.adminRole,
  };

  return (
    <AuthContext.Provider value={value}>
      {children}
    </AuthContext.Provider>
  );
};

export const useAuth = () => {
  const ctx = useContext(AuthContext);
  if (!ctx) throw new Error('useAuth must be inside AuthProvider');
  return ctx;
};

export default AuthContext;
